import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  User, 
  Mail, 
  Lock, 
  MapPin,
  Loader2, 
  AlertCircle, 
  ArrowLeft,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const countries = [
  { id: 'tanzania', name: 'Tanzania', flag: '🇹🇿' },
  { id: 'ghana', name: 'Ghana', flag: '🇬🇭' },
  { id: 'malawi', name: 'Malawi', flag: '🇲🇼' },
  { id: 'rwanda', name: 'Rwanda', flag: '🇷🇼' },
  { id: 'mali', name: 'Mali', flag: '🇲🇱' },
  { id: 'burkina-faso', name: 'Burkina Faso', flag: '🇧🇫' },
  { id: 'mozambique', name: 'Mozambique', flag: '🇲🇿' },
];

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: form, 2: verify email, 3: pending approval
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.country) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/contributor/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          country: formData.country
        })
      });

      const data = await response.json();

      if (response.ok) {
        setVerificationToken(data.verification_token);
        setStep(2);
      } else {
        setError(data.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email?token=${verificationToken}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        setStep(3);
      } else {
        setError(data.detail || 'Verification failed');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify Email
  if (step === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#07743F] to-[#065a32] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-[#72BF44] rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h1>
            <p className="text-gray-600 mb-6">
              We've sent a verification link to <strong>{formData.email}</strong>
            </p>
            
            {/* Demo: Direct verify button (remove in production) */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-xs text-amber-800 mb-3">
                <strong>Demo Mode:</strong> Click below to verify immediately
              </p>
              <Button
                onClick={handleVerifyEmail}
                disabled={loading}
                className="bg-[#07743F] hover:bg-[#065a32] text-white w-full"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>
                ) : (
                  'Verify Email Now'
                )}
              </Button>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <Button 
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="text-gray-600"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Pending Approval
  if (step === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#07743F] to-[#065a32] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-[#72BF44] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
            <p className="text-gray-600 mb-6">
              Your account is now <strong>pending admin approval</strong>.
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-green-900 mb-2">What happens next?</h3>
              <ul className="text-sm text-green-800 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                  Our team reviews your registration
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                  Once approved, you'll receive an email
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-green-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                  Then you can log in and submit stories
                </li>
              </ul>
            </div>

            <Button 
              onClick={() => navigate('/')}
              className="bg-[#07743F] hover:bg-[#065a32] text-white w-full"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 1: Registration Form
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#07743F] to-[#065a32] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="text-white/80 hover:text-white hover:bg-white/10 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <Card className="border-0 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#07743F] rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Become a Contributor</h1>
              <p className="text-gray-600 mt-2">
                Share your story and inspire young farmers across Africa
              </p>
            </div>

            <form onSubmit={handleRegister} className="space-y-5">
              <div>
                <Label className="flex items-center gap-2 text-gray-700 mb-2">
                  <User className="w-4 h-4" />
                  Full Name
                </Label>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="border-gray-300 focus:border-[#72BF44]"
                  data-testid="register-name"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 text-gray-700 mb-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="border-gray-300 focus:border-[#72BF44]"
                  data-testid="register-email"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 text-gray-700 mb-2">
                  <MapPin className="w-4 h-4" />
                  Your Country
                </Label>
                <div className="grid grid-cols-4 gap-2">
                  {countries.map(country => (
                    <button
                      key={country.id}
                      type="button"
                      onClick={() => handleChange('country', country.id)}
                      className={`p-2 rounded-lg border-2 text-center transition-all ${
                        formData.country === country.id
                          ? 'border-[#07743F] bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-xl block">{country.flag}</span>
                      <span className="text-xs text-gray-700">{country.name.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2 text-gray-700 mb-2">
                  <Lock className="w-4 h-4" />
                  Password
                </Label>
                <Input
                  type="password"
                  placeholder="At least 6 characters"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className="border-gray-300 focus:border-[#72BF44]"
                  data-testid="register-password"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 text-gray-700 mb-2">
                  <Lock className="w-4 h-4" />
                  Confirm Password
                </Label>
                <Input
                  type="password"
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className="border-gray-300 focus:border-[#72BF44]"
                  data-testid="register-confirm-password"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#07743F] hover:bg-[#065a32] text-white py-5"
                data-testid="register-submit"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Account...</>
                ) : (
                  <>Create Account <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-gray-600 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-[#07743F] font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
